import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Select, SelectProps } from 'antd';
import { CategoryModal, CategoryModalRef } from './CategoryModal';
import { getCategoryThingModel } from '@/api/category-template';

export interface CategorySelectProps<ValueType = any> extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  onCategoryKeyChange?: ((category_key: string) => void) | undefined;
}

// localStorage 的 key
const CATEGORY_STORAGE_KEY = 'product_category_cache';

// 从 localStorage 获取品类信息
const getCategoryFromStorage = (categoryId: string): { id: string; name: string } | null => {
  try {
    const stored = localStorage.getItem(CATEGORY_STORAGE_KEY);
    if (stored) {
      const categories = JSON.parse(stored);
      return categories[categoryId] || null;
    }
  } catch (error) {
    console.error('从 localStorage 读取品类信息失败:', error);
  }
  return null;
};

// 保存品类信息到 localStorage
const saveCategoryToStorage = (category: { id: string; name: string }) => {
  try {
    const stored = localStorage.getItem(CATEGORY_STORAGE_KEY);
    const categories = stored ? JSON.parse(stored) : {};
    categories[category.id] = { id: category.id, name: category.name };
    localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(categories));
  } catch (error) {
    console.error('保存品类信息到 localStorage 失败:', error);
  }
};

export const CategorySelect: React.FC<CategorySelectProps> = (props) => {
  const categoryModalRef = useRef<CategoryModalRef>(null);
  const [innerValue, setInnerValue] = useState<string>();
  const [options, setOptions] = useState<SelectProps['options']>();
  const [loading, setLoading] = useState<boolean>(false);

  // 同步外部传入的 value
  useEffect(() => {
    if (props.value !== undefined) {
      setInnerValue(props.value as string | undefined);
      // 如果 value 存在，先从 localStorage 读取品类信息
      if (props.value) {
        const savedCategory = getCategoryFromStorage(props.value);
        if (savedCategory) {
          setOptions([{ label: savedCategory.name, value: savedCategory.id }]);
        }
      }
    } else {
      // 如果外部值为 undefined，清空内部值
      setInnerValue(undefined);
      setOptions(undefined);
    }
  }, [props.value]);

  // 当 value 存在但 options 为空时，根据 value 获取品类信息
  const getDefaultOptions = useCallback(async (categoryId: string) => {
    // 先检查 localStorage 中是否有保存的值，如果有就直接使用，不需要请求
    const savedCategory = getCategoryFromStorage(categoryId);
    if (savedCategory) {
      setOptions([{ label: savedCategory.name, value: savedCategory.id }]);
      return;
    }

    // 如果 options 中已经有对应的值，也不需要请求
    if (options?.some((opt) => opt.value === categoryId)) {
      return;
    }

    // 只有在 localStorage 中也没有值的情况下，才请求 API
    setLoading(true);
    try {
      const resp = await getCategoryThingModel(categoryId);
      if (resp.success && resp.result) {
        const category = resp.result;
        // 保存品类信息到 localStorage
        saveCategoryToStorage({ id: category.id, name: category.name });
        setOptions([{ label: category.name, value: category.id }]);
        props.onCategoryKeyChange?.(category.id);
      }
    } catch (error) {
      console.error('获取品类信息失败:', error);
    } finally {
      setLoading(false);
    }
  }, [props.onCategoryKeyChange, options]);

  useEffect(() => {
    // 当 value 存在但 options 中没有对应的选项时，需要获取品类信息
    if (innerValue && !options?.some((opt) => opt.value === innerValue)) {
      // 先检查 localStorage 中是否有保存的值，如果有就直接使用，不需要请求
      const savedCategory = getCategoryFromStorage(innerValue);
      if (savedCategory) {
        setOptions([{ label: savedCategory.name, value: savedCategory.id }]);
        return;
      }
      // 只有在 localStorage 中也没有值的情况下，才请求接口
      getDefaultOptions(innerValue);
    }
  }, [innerValue, options, getDefaultOptions]);

  // 只有当 options 中存在对应的 value 时才显示 value，否则显示 placeholder
  const displayValue = options?.some((opt) => opt.value === innerValue) ? innerValue : undefined;

  return (
    <>
      <CategoryModal
        ref={categoryModalRef}
        selectedValue={innerValue}
        onChange={({ id, category_key, category_name }) => {
          // 用户重新选择时，保存品类信息到 localStorage
          saveCategoryToStorage({ id, name: category_name });
          setInnerValue(id);
          setOptions([{ label: category_name, value: id }]);
          props.onChange?.(id, []);
          props.onCategoryKeyChange?.(category_key);
        }}
      />
      <Select
        {...props}
        open={false}
        options={options}
        value={displayValue}
        loading={loading}
        placeholder="请选择品类"
        onClick={() => {
          categoryModalRef.current?.open();
        }}
      />
    </>
  );
};

export default CategorySelect;
